import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useOps } from '../../../../../hooks/useOps';
import {
  formatCurrencyBrlFromCents,
  formatDateIso,
  formatPhone,
  formatCnpj,
  formatCpf,
} from '../../../../../utils';
import {
  FechamentoOperacaoRow,
  FechamentoResponse,
  FechamentoResumoRow,
  NfsListResponse,
  SRListResponse,
  SRRow,
} from '../types';
import { romaneioService } from '../../../../../api';

// ── Week option type ──────────────────────────────────────────────────────────

export interface WeekOption {
  segunda: string;
  domingo: string;
  label: string;
  isCurrentWeek: boolean;
}

// ── Date helpers (frontend, local time) ──────────────────────────────────────

function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun
  const diff = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - diff);
  return d;
}

function dateStrFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysToDate(d: Date, days: number): Date {
  const result = new Date(d);
  result.setDate(d.getDate() + days);
  return result;
}

function formatWeekLabel(segunda: string, domingo: string): string {
  const [sy, sm, sd] = segunda.split('-').map(Number);
  const [dy, dm, dd] = domingo.split('-').map(Number);
  const p = (n: number) => String(n).padStart(2, '0');
  if (sy === dy) {
    return `${p(sd)}/${p(sm)} – ${p(dd)}/${p(dm)}/${dy}`;
  }
  return `${p(sd)}/${p(sm)}/${sy} – ${p(dd)}/${p(dm)}/${dy}`;
}

function generateWeekOptions(numPastWeeks = 12): WeekOption[] {
  const today = new Date();
  const currentMonday = getMondayOfWeek(today);
  const currentSeconda = dateStrFromDate(currentMonday);
  const options: WeekOption[] = [];

  // semana atual primeiro
  const currentDomingo = dateStrFromDate(addDaysToDate(currentMonday, 6));
  options.push({
    segunda: currentSeconda,
    domingo: currentDomingo,
    label: `Semana atual — ${formatWeekLabel(currentSeconda, currentDomingo)}`,
    isCurrentWeek: true,
  });

  // semanas anteriores
  for (let i = 1; i <= numPastWeeks; i++) {
    const monday = addDaysToDate(currentMonday, -7 * i);
    const segunda = dateStrFromDate(monday);
    const domingo = dateStrFromDate(addDaysToDate(monday, 6));
    options.push({ segunda, domingo, label: formatWeekLabel(segunda, domingo), isCurrentWeek: false });
  }

  return options;
}

// ── Query keys ────────────────────────────────────────────────────────────────

const fechamentoQueryKeys = {
  all: ['entrego-fechamento'] as const,
  byOpsAndWeek: (ops: string, segunda: string) =>
    [...fechamentoQueryKeys.all, ops, segunda] as const,
  nfsByOpsAndWeek: (ops: string, segunda: string) =>
    ['entrego-nfs', ops, segunda] as const,
  srByOpsAndWeek: (ops: string, segunda: string) =>
    ['entrego-sr', ops, segunda] as const,
};

async function fetchFechamento(
  ops: string,
  segunda: string,
  allowCurrentWeek: boolean
): Promise<FechamentoResponse | null> {
  const response = await romaneioService.getEntregoFechamento({
    ops,
    segunda,
    ...(allowCurrentWeek ? { allowCurrentWeek: true } : {}),
  });
  if (response.success) {
    return (response.data as unknown as FechamentoResponse) ?? null;
  }
  throw new Error(response.message || 'Erro ao carregar dados de fechamento');
}

async function fetchNFSList(
  ops: string,
  segunda: string,
  allowCurrentWeek: boolean
): Promise<NfsListResponse | null> {
  const response = await romaneioService.getEntregoNFSList({
    ops,
    segunda,
    ...(allowCurrentWeek ? { allowCurrentWeek: true } : {}),
  });
  if (response.success) {
    return (response.data as unknown as NfsListResponse) ?? null;
  }
  throw new Error(response.message || 'Erro ao carregar lista de NFS');
}

async function fetchSRList(
  ops: string,
  segunda: string,
  allowCurrentWeek: boolean
): Promise<SRListResponse | null> {
  const response = await romaneioService.getEntregoSRList({
    ops,
    segunda,
    ...(allowCurrentWeek ? { allowCurrentWeek: true } : {}),
  });
  if (response.success) {
    return (response.data as unknown as SRListResponse) ?? null;
  }
  throw new Error(response.message || 'Erro ao carregar lista de SR');
}

const downloadCsv = (rows: Record<string, unknown>[], filename: string) => {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const v = String(row[h] ?? '');
          return v.includes(',') || v.includes('"') || v.includes('\n')
            ? `"${v.replace(/"/g, '""')}"`
            : v;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Formata CPF (11 dígitos) ou CNPJ (14 dígitos), retorna original se não encaixar */
const formatDoc = (doc: string): string => {
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return formatCpf(digits);
  if (digits.length === 14) return formatCnpj(digits);
  return doc;
};

export function useEntregoFechamento() {
  const { currentOps } = useOps();

  // ── Week selection ────────────────────────────────────────────────────────
  const weekOptions = useMemo(() => generateWeekOptions(12), []);
  // Default: semana anterior (índice 1)
  const [selectedSeconda, setSelectedSeconda] = useState<string>(weekOptions[1].segunda);

  const selectedWeekOption = weekOptions.find((w) => w.segunda === selectedSeconda) ?? weekOptions[1];
  const isCurrentWeek = selectedWeekOption.isCurrentWeek;

  const enabled = Boolean(currentOps);

  const query = useQuery({
    queryKey: fechamentoQueryKeys.byOpsAndWeek(currentOps || '', selectedSeconda),
    queryFn: () => fetchFechamento(currentOps!, selectedSeconda, isCurrentWeek),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const nfsQuery = useQuery({
    queryKey: fechamentoQueryKeys.nfsByOpsAndWeek(currentOps || '', selectedSeconda),
    queryFn: () => fetchNFSList(currentOps!, selectedSeconda, isCurrentWeek),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const srQuery = useQuery({
    queryKey: fechamentoQueryKeys.srByOpsAndWeek(currentOps || '', selectedSeconda),
    queryFn: () => fetchSRList(currentOps!, selectedSeconda, isCurrentWeek),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const nfsData = nfsQuery.data ?? null;
  const srData = srQuery.data ?? null;

  const data = query.data ?? null;

  const buildFilename = useCallback(
    (prefix: string, ext: string) => {
      if (!data) return `${prefix}.${ext}`;
      const { semanaCompleta, periodo } = data;
      // A semana atual nunca está "fechada" mesmo que todos os dias até hoje tenham dados —
      // a semana ainda está em andamento, então o aviso sempre se aplica.
      const weekClosed = semanaCompleta && !isCurrentWeek;
      const suffix = weekClosed ? '' : '_SEMANA_NAO_FECHADA';
      return `${prefix}_${currentOps}_${periodo.segunda}_${periodo.domingo}${suffix}.${ext}`;
    },
    [data, currentOps, isCurrentWeek]
  );

  const handleDownloadResumo = useCallback(() => {
    if (!data) return;
    const rows = data.resumo.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      ifood_uuid: r.ifoodUuid ?? '',
      telefone: r.telefone,
      valor_face_agregado: r.valorFaceAgregado,
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('resumo', 'csv'));
  }, [data, buildFilename]);

  const handleDownloadOperacoes = useCallback(() => {
    if (!data) return;
    const rows = data.operacoes.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      cnpj: r.cnpj ?? '',
      ifood_uuid: r.ifoodUuid ?? '',
      telefone: r.telefone,
      valor_face: r.valorFace,
      valor_compra: r.valorCompra,
      data_romaneio: r.dataRomaneio?.substring(0, 10) ?? '',
      data_vencimento: r.dataVencimento?.substring(0, 10) ?? '',
      data_solicitado: r.dataSolicitado?.substring(0, 19) ?? '',
      comprovante_bancario: r.comprovanteBancario ?? '',
      tipo_emissao_nf: r.tipoEmissaoNf ?? '',
      url_nfs: r.urlNfs ?? '',
      chave_nfs: r.chaveNfs ?? '',
    }));
    downloadCsv(
      rows as Record<string, unknown>[],
      buildFilename('operacoes', 'csv')
    );
  }, [data, buildFilename]);

  const [nfsZipLoading, setNfsZipLoading] = useState(false);
  const [nfsZipJobKey, setNfsZipJobKey] = useState<string | null>(null);
  const [nfsZipChecking, setNfsZipChecking] = useState(false);
  const [nfsZipError, setNfsZipError] = useState<string | null>(null);

  /** Passo 1 — solicita a geração assíncrona do ZIP via SQS */
  const handleRequestNFSZip = useCallback(async () => {
    const hasNfs = nfsData && (nfsData.totalAntecipacao + nfsData.totalSaldoRemanescente) > 0;
    if (!hasNfs || !currentOps) return;
    setNfsZipLoading(true);
    setNfsZipError(null);
    try {
      const response = await romaneioService.getEntregoNFSZip({
        ops: currentOps,
        segunda: selectedSeconda,
        ...(isCurrentWeek ? { allowCurrentWeek: true } : {}),
      });
      if (response.success && (response.data as any)?.jobKey) {
        setNfsZipJobKey((response.data as any).jobKey as string);
      } else {
        setNfsZipError('Não foi possível iniciar a geração do ZIP.');
      }
    } catch (err: any) {
      // 409 = nem todas as notas do período foram baixadas ainda (cron incremental);
      // o backend explica quantas faltam — mostra a mensagem dele.
      const backendMessage = err?.response?.data?.message;
      setNfsZipError(
        typeof backendMessage === 'string' && backendMessage
          ? backendMessage
          : 'Erro ao solicitar geração do ZIP.'
      );
    } finally {
      setNfsZipLoading(false);
    }
  }, [nfsData, currentOps, selectedSeconda, isCurrentWeek]);

  /** Passo 2 — verifica se o ZIP está pronto e abre o download */
  const handleCheckNFSZip = useCallback(async () => {
    if (!nfsZipJobKey || !currentOps) return;
    setNfsZipChecking(true);
    setNfsZipError(null);
    try {
      const response = await romaneioService.getEntregoNFSZipStatus({
        ops: currentOps,
        jobKey: nfsZipJobKey,
      });
      if (response.success) {
        const d = response.data as any;
        if (d?.ready && d?.zipUrl) {
          window.open(d.zipUrl, '_blank', 'noopener,noreferrer');
        } else {
          setNfsZipError('ZIP ainda não está pronto. Tente novamente em alguns segundos.');
        }
      } else {
        setNfsZipError('Erro ao verificar o status do ZIP.');
      }
    } catch {
      setNfsZipError('Erro ao verificar o status do ZIP.');
    } finally {
      setNfsZipChecking(false);
    }
  }, [nfsZipJobKey, currentOps]);

  const nfsZipReset = useCallback(() => {
    setNfsZipJobKey(null);
    setNfsZipError(null);
  }, []);

  // ── SR download ──────────────────────────────────────────────────────────────
  const handleDownloadSR = useCallback(() => {
    if (!srData || srData.registros.length === 0 || !data) return;
    const rows = srData.registros.map((r) => ({
      sol_id: r.solId,
      cnpj_cpf: r.cnpjCpf,
      cpf: r.cpf ?? '',
      nome: r.nome ?? '',
      telefone: r.telefone ?? '',
      ifood_uuid: r.ifoodUuid ?? '',
      ops: r.ops,
      tipo_emissao_nf: r.tipoEmissaoNf ?? '',
      tomador_cnpj: r.tomadorCnpj ?? '',
      total_semanal_sem_gorjeta: r.totalSemanalSemGorjeta,
      gorjeta: r.gorjeta,
      valor_antecipado_com_nfs: r.valorAntecipadoComNfs,
      valor_antecipado_emitir_nfs: r.valorAntecipadoEmitirNfs,
      saldo_remanescente_emitir_nfs: r.saldoRemanescenteEmitirNfs,
      data_periodo_inicio: r.dataPeriodoInicio,
      data_periodo_fim: r.dataPeriodoFim,
      descricao: `Saldo Remanescente período - ${r.dataPeriodoInicio} a ${r.dataPeriodoFim}`,
      chave_nfs: r.chaveNfs ?? '',
      url_nfs: r.urlNfs ?? '',
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('saldo_remanescente', 'csv'));
  }, [srData, data, buildFilename]);

  // ── SR columns ───────────────────────────────────────────────────────────────
  const srColumns: ColumnDef<SRRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'CNPJ/CPF',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatCpf(v.replace(/\D/g, ''))}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'nome',
        header: 'Nome',
        size: 220,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatPhone(v)}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'ifoodUuid',
        header: 'UUID iFood',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-xs text-[hsl(215,16%,40%)]">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'tomadorCnpj',
        header: 'Tomador CNPJ',
        size: 160,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatCnpj(v.replace(/\D/g, ''))}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'tipoEmissaoNf',
        header: 'Tipo NF',
        size: 110,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="text-sm">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'totalSemanalSemGorjeta',
        header: 'Total Semanal',
        size: 140,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'gorjeta',
        header: 'Gorjeta',
        size: 120,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="text-sm text-emerald-600">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'valorAntecipadoComNfs',
        header: 'Antecipado c/ NFS',
        size: 155,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="text-sm text-blue-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'valorAntecipadoEmitirNfs',
        header: 'Antecipado emitir NFS',
        size: 175,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="text-sm text-amber-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'saldoRemanescenteEmitirNfs',
        header: 'SR a emitir NFS',
        size: 155,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-violet-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'dataPeriodoInicio',
        header: 'Início Período',
        size: 130,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataPeriodoFim',
        header: 'Fim Período',
        size: 130,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'urlNfs',
        header: 'Nota Fiscal',
        size: 140,
        cell: ({ row }) => {
          const url = row.original.urlNfs;
          const chave = row.original.chaveNfs;
          if (url) {
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={chave ?? undefined}
                className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,22%)] underline inline-flex items-center gap-1 text-sm font-medium"
              >
                Ver NFS
                <ExternalLink className="h-3 w-3" />
              </a>
            );
          }
          return <span className="text-[hsl(215,16%,70%)] text-sm italic">Sem NFS</span>;
        },
      },
    ],
    []
  );

  // ── Summary stats ────────────────────────────────────────────────────────────
  const summaryStats = useMemo(() => {
    if (!data) return null;
    const totalEntregadores = data.resumo.length;
    const totalOperacoes = data.operacoes.length;
    const valorFaceTotal = data.resumo.reduce((acc, r) => acc + r.valorFaceAgregado, 0);
    const comComprovante = data.operacoes.filter((o) => o.comprovanteBancario).length;
    return { totalEntregadores, totalOperacoes, valorFaceTotal, comComprovante };
  }, [data]);

  // ── Colunas Resumo ───────────────────────────────────────────────────────────
  const resumoColumns: ColumnDef<FechamentoResumoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatCpf(v.replace(/\D/g, ''))}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'ifoodUuid',
        header: 'UUID iFood',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-xs text-[hsl(215,16%,40%)]">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFaceAgregado',
        header: 'Valor Face Total',
        size: 160,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
    ],
    []
  );

  // ── Colunas Operações ────────────────────────────────────────────────────────
  const operacoesColumns: ColumnDef<FechamentoOperacaoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'ifoodUuid',
        header: 'UUID iFood',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-xs text-[hsl(215,16%,40%)]">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFace',
        header: 'Valor Face',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'valorCompra',
        header: 'Valor Compra',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-blue-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'dataRomaneio',
        header: 'Romaneio',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataVencimento',
        header: 'Vencimento',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataSolicitado',
        header: 'Solicitado em',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="text-sm tabular-nums">{formatDateIso(v)}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'comprovanteBancario',
        header: 'Comprovante',
        size: 150,
        cell: ({ getValue }) => {
          const url = getValue() as string | null;
          if (!url) {
            return <span className="text-[hsl(215,16%,70%)] text-sm">Sem comprovante</span>;
          }
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,22%)] underline inline-flex items-center gap-1 text-sm font-medium"
            >
              Ver comprovante
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        },
      },
      {
        accessorKey: 'urlNfs',
        header: 'Nota Fiscal',
        size: 140,
        cell: ({ row }) => {
          const url = row.original.urlNfs;
          const chave = row.original.chaveNfs;
          if (url) {
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={chave ?? undefined}
                className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,22%)] underline inline-flex items-center gap-1 text-sm font-medium"
              >
                Ver NFS
                <ExternalLink className="h-3 w-3" />
              </a>
            );
          }
          return <span className="text-[hsl(215,16%,70%)] text-sm italic">Sem NFS</span>;
        },
      },
    ],
    []
  );

  return {
    currentOps,
    data,
    summaryStats,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error?.message || 'Erro ao conectar com o servidor' : '',
    resumoColumns,
    operacoesColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    loadData: () => { query.refetch(); nfsQuery.refetch(); srQuery.refetch(); },
    // Week selection
    weekOptions,
    selectedSeconda,
    setSelectedSeconda,
    selectedWeekOption,
    isCurrentWeek,
    // NFS
    nfsData,
    handleRequestNFSZip,
    handleCheckNFSZip,
    nfsZipLoading,
    nfsZipJobKey,
    nfsZipChecking,
    nfsZipError,
    nfsZipReset,
    nfsLoading: nfsQuery.isLoading,
    nfsFetching: nfsQuery.isFetching,
    nfsError: nfsQuery.isError ? nfsQuery.error?.message || 'Erro ao carregar NFS' : '',
    srSemaforoStatus: nfsData?.srSemaforoStatus ?? null,
    // SR
    srData,
    srColumns,
    handleDownloadSR,
    srLoading: srQuery.isLoading,
    srFetching: srQuery.isFetching,
    srError: srQuery.isError ? srQuery.error?.message || 'Erro ao carregar SR' : '',
  };
}
